<template>
  <BaseButtonDropdown
    v-model="pickerOpen"
    class="c-date-picker"
    data-testid="input-date-picker"
    :dropdown-class="`c-date-picker__dropdown ${dropdownClass}`"
    :fallback-placements="fallbackPlacements"
    flip
    new-design
    new-dropdown
    :placement="placement"
    :teleport="localTeleport"
    trap-focus
  >
    <template #btn="{ toggle }">
      <slot
        :after-label="afterLabel"
        :before-label="beforeLabel"
        :label="buttonLabel"
        name="btn"
        :open="pickerOpen"
        :toggle="toggle"
      >
        <BaseButtonGroup>
          <BaseButton
            :active="active"
            :active-color="activeColor"
            class="c-date-picker__button"
            data-testid="input-date-picker-button"
            :disabled="disabled"
            icon="schedule-post"
            :label="buttonLabel"
            new-design
            new-icon
            v-bind="inheritedAttributes"
            @click="toggle"
          />
          <BaseButton
            v-if="clearable && hasValue"
            data-testid="input-date-picker-clear"
            :disabled="disabled"
            icon="close"
            new-design
            new-icon
            v-bind="inheritedAttributes"
            @click="clear"
          />
        </BaseButtonGroup>
      </slot>
    </template>
    <BaseInputDate
      :disabled="disabled"
      :hide-user-time-zone="hideUserTimeZone"
      :model-value="modelValue"
      :range="range"
      :range-selection="rangeSelection"
      :use-local-time-zone="useLocalTimeZone"
      v-bind="inheritedAttributes"
      @cancel="cancelRange"
      @update:model-value="updateDate"
      @update:range="updateRange"
      @updated="updated"
    >
      <template
        v-for="(_, name) in $slots"
        #[name]="slotData"
      >
        <slot
          :name="name"
          v-bind="slotData"
        />
      </template>
    </BaseInputDate>
  </BaseButtonDropdown>
</template>

<script setup lang="ts">
import { format } from "date-fns";
import { omit } from "lodash-es";
import { storeToRefs } from "pinia";
import { computed, ref, type TeleportProps, useAttrs, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import type { PopperProps } from "shared/components/base/BasePopper";
import { useModalOverlayTarget } from "shared/components/core/modals/useModalOverlayTarget";
import type {
  CalendarModel,
  CalendarProps,
} from "shared/composables/useCalendar";
import {
  formatDate,
  formatIntlDate,
  fromCurrentToGivenTimezone,
  getTimezone,
} from "shared/helpers/date";
import DateRange from "shared/helpers/DateRange";
import { useUserStore } from "shared/stores/user";
import type { Nullable } from "shared/types";

import BaseButton from "../BaseButton";
import BaseButtonDropdown from "../BaseButtonDropdown";
import BaseButtonGroup from "../BaseButtonGroup";
import BaseInputDate from "../BaseInputDate";
import { useInputDateReflow } from "../BaseInputDate/useInputDateReflow";

export interface InputDatePickerProps {
  active?: boolean;
  activeColor?: string;
  clearable?: boolean;
  closeOnUpdate?: boolean;
  dateFormat?: Nullable<string>;
  dateStyle?: Nullable<Intl.DateTimeFormatOptions["dateStyle"]>;
  disabled?: boolean;
  fallbackPlacements?: PopperProps["fallbackPlacements"];
  forceRange?: boolean;
  formatOptions?: Nullable<Intl.DateTimeFormatOptions>;
  hideUserTimeZone?: boolean;
  label?: string;
  placeholder?: string;
  placement?: PopperProps["placement"];
  range?: Nullable<CalendarProps["range"]>;
  rangeFormat?: Nullable<string>;
  rangeSelection?: CalendarProps["rangeSelection"];
  teleport?: TeleportProps["to"];
  timeStyle?: Nullable<Intl.DateTimeFormatOptions["timeStyle"]>;
  useLocalTimeZone?: boolean;
}

const model = defineModel<CalendarModel>({
  default: null,
});

const props = withDefaults(defineProps<InputDatePickerProps>(), {
  dateFormat: null,
  rangeFormat: null,
  rangeSelection: "day",
  dateStyle: null,
  timeStyle: null,
  formatOptions: null,
  range: null,
  label: "",
  placeholder: "",
  placement: "bottom-start",
  fallbackPlacements: () => ["left", "top-start", "top-end"],
  teleport: "body",
  activeColor: "activated",
});

const emit = defineEmits<{
  cancel: [];
  open: [];
  "update:range": [DateRange | null];
  updated: [];
}>();

const attrs = useAttrs();

const userStore = useUserStore();
const { currentUser } = storeToRefs(userStore);

const pickerOpen = ref(false);

const { isReflowed } = useInputDateReflow();

const dropdownClass = computed<string>(() =>
  isReflowed.value ? "c-date-picker__dropdown--reflowed" : ""
);

const modalOverlayTarget = useModalOverlayTarget();

// The modal container opens a z-8000 stacking context, so a panel sent to
// `body` lands under the modal that owns it. Its own container clears the
// modals (z-3000+) without leaving their order.
const localTeleport = computed<TeleportProps["to"]>(() => {
  const target = isReflowed.value ? "body" : props.teleport;

  if (target !== "body") {
    return target;
  }

  return modalOverlayTarget?.value ?? "body";
});

const localDateTimeStyle = computed<Intl.DateTimeFormatOptions | null>(() => {
  if (!props.dateStyle && !props.timeStyle) {
    return null;
  }

  const style: Intl.DateTimeFormatOptions = {};

  if (props.dateStyle) {
    style.dateStyle = props.dateStyle;
  }

  if (props.timeStyle) {
    style.timeStyle = props.timeStyle;
  }

  return style;
});

const localFormatOptions = computed<Intl.DateTimeFormatOptions>(() => {
  if (props.formatOptions) {
    return props.formatOptions;
  }

  if (localDateTimeStyle.value) {
    return localDateTimeStyle.value;
  }

  if (props.rangeSelection !== "day") {
    return {
      month: "short",
      day: "numeric",
    };
  }

  return {
    dateStyle: "short",
  };
});

const localTimeZone = computed<string>(() =>
  props.useLocalTimeZone ? getTimezone() : currentUser.value.time_zone
);

const hasSameTimezone = computed<boolean>(
  () => getTimezone() === currentUser.value.time_zone
);

const localValue = computed<Date | null>(() => {
  if (model.value) {
    return fromCurrentToGivenTimezone(model.value, localTimeZone.value);
  }

  return null;
});

const localRange = computed<DateRange | null>(() => {
  if (props.range) {
    return DateRange.fromDates(
      fromCurrentToGivenTimezone(props.range.after * 1000, localTimeZone.value),
      fromCurrentToGivenTimezone(props.range.before * 1000, localTimeZone.value)
    );
  }

  return null;
});

const inheritedAttributes = computed<Omit<typeof attrs, "class" | "style">>(
  () => omit(attrs, ["class", "style"])
);

const buttonLabel = computed<string>(() => {
  if (props.label) {
    return props.label;
  }

  if (localValue.value) {
    if (props.dateFormat) {
      return format(localValue.value, props.dateFormat);
    }

    return formatIntlDate(localValue.value, localFormatOptions.value);
  }

  if (localRange.value) {
    if (hasSameTimezone.value && props.range) {
      if (props.rangeFormat) {
        return props.range.toString(props.rangeFormat, {
          forceRange: props.forceRange,
        });
      }

      return props.range.toIntlString(localFormatOptions.value);
    }

    const after = new Date(localRange.value.after * 1000);
    const before = new Date(localRange.value.before * 1000);

    if (props.rangeFormat) {
      return getLocaleText("date.pretty_ranges.range", {
        after: format(after, props.rangeFormat),
        before: format(before, props.rangeFormat),
      });
    }

    return getLocaleText("date.pretty_ranges.range", {
      after: formatIntlDate(after, localFormatOptions.value),
      before: formatIntlDate(before, localFormatOptions.value),
    });
  }

  if (props.placeholder) {
    return props.placeholder;
  }

  return "";
});

const afterLabel = computed<string>(() => {
  if (localRange.value) {
    if (props.dateFormat) {
      return formatDate(localRange.value.after, props.dateFormat);
    }

    return formatIntlDate(localRange.value.after, localFormatOptions.value);
  }

  return "";
});

const beforeLabel = computed<string>(() => {
  if (localRange.value) {
    if (props.dateFormat) {
      return formatDate(localRange.value.before, props.dateFormat);
    }

    return formatIntlDate(localRange.value.before, localFormatOptions.value);
  }

  return "";
});

const hasValue = computed<boolean>(() => Boolean(model.value || props.range));

watch(pickerOpen, (value) => {
  if (value) {
    emit("open");
  }
});

function updateDate(date: CalendarModel): void {
  model.value = date;

  if (props.closeOnUpdate) {
    pickerOpen.value = false;
  }
}

function updateRange(range: DateRange | null) {
  emit("update:range", range);

  if (props.closeOnUpdate) {
    pickerOpen.value = false;
  }
}

function cancelRange(): void {
  pickerOpen.value = false;
  emit("cancel");
}

function updated() {
  pickerOpen.value = false;
  emit("updated");
}

function clear() {
  model.value = null;
  emit("update:range", null);
}

function open() {
  pickerOpen.value = true;
}

defineExpose({
  open,
});
</script>

<style lang="scss" scoped>
.c-date-picker {
  display: flex;
  flex-direction: column;

  &__button {
    flex: 1;

    :deep() {
      span:last-child {
        flex: 1;
        text-align: left;
      }
    }
  }
}
</style>

<style lang="scss">
// At 320x256 floating-ui offers an anchored panel 85px and clips it off both
// edges, so it becomes a sheet across the bottom instead (WCAG 1.4.10 Reflow).
.c-date-picker__dropdown--reflowed {
  border-bottom: none;
  border-inline: none;
  border-radius: var(--s-border-radius-md) var(--s-border-radius-md) 0 0;
  inset: auto 0 0 !important;
  max-height: 100dvh;
  max-width: none !important;
  overflow: hidden;
  padding-block: 0;
  position: fixed !important;
  transform: none !important;
  width: auto !important;
}
</style>
